"use client";

import { memo, useRef } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { Eye, Heart, Laptop, Image as ImageIcon, LinkIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  formatLibraryName,
  getLibraryColor,
} from "@/lib/colors/library-colors";
import { ComponentPreview } from "@/app/(app)/components/_components/component-preview";
import type { BlockConfig } from "@/lib/registry/types";
import type { ComponentPreviewData } from "./page-wrapper"; // Assuming page-wrapper exports this type
import { DynamicDemoRenderer } from "./dynamic-demo-renderer";

// Define item type with optional preview props
export type Item = BlockConfig & {
  id: string;
  library?: string;
  name: string;
  description?: string;
  categories?: string[];
  tags?: string[];
  previewProps?: {
    previewPath: string;
    hasPreviewImage: boolean;
    availablePreviewFiles: string[];
    demoFiles?: string[];
  };
};

interface ComponentCardProps {
  item: Item;
  previewData: ComponentPreviewData | undefined;
  showDemo: boolean;
  toggleStar: (id: string) => void;
  isItemStarred: boolean;
  style?: React.CSSProperties;
}

export const ComponentCard = memo(
  ({
    item,
    previewData,
    showDemo,
    toggleStar,
    isItemStarred,
    style,
  }: ComponentCardProps) => {
    const ref = useRef<HTMLDivElement>(null); // Ref for potential future use (e.g., intersection observer if needed outside react-window)

    const name = formatLibraryName(item.name);
    const hasDemos = previewData?.hasDemos || false;
    const imageProps = previewData?.previews.imageProps;
    const isDynamicComponent =
      previewData?.previews.demo === "dynamic" && previewData?.component;

    return (
      <motion.div
        ref={ref}
        // Layout animations can be added here if desired, e.g., layoutId={item.id}
        className="group flex flex-col h-full"
        style={style} // Apply style passed from react-window
      >
        <div className="rounded-lg overflow-hidden shadow-md transition-all duration-300 border bg-card">
          <div className="relative flex aspect-video w-full items-center justify-center overflow-hidden border-b bg-background">
            {/* Always render the image preview if available */}
            {imageProps && (
              <div className="z-10 h-full w-full flex items-center justify-center p-4 overflow-hidden">
                <ComponentPreview {...imageProps} />
              </div>
            )}

            {/* Conditionally render and fade in the demo on top */}
            {hasDemos && (
              <motion.div
                className="absolute inset-0 z-20 h-full w-full flex items-center justify-center p-4 overflow-hidden bg-background"
                initial={{ opacity: 0 }}
                animate={{ opacity: showDemo ? 1 : 0 }}
                transition={{ duration: 0.3 }}
                style={{ pointerEvents: showDemo ? "auto" : "none" }} // Allow interaction only when demo is visible
              >
                {isDynamicComponent ? (
                  <DynamicDemoRenderer component={previewData!.component!} />
                ) : (
                  previewData?.previews.demo
                )}
              </motion.div>
            )}

            {/* Demo indicator icon */}
            {/* {hasDemos && (
              <div className="absolute left-2 top-2 z-30 px-2 py-1 text-xs font-medium rounded-full bg-background/80 backdrop-blur-sm border">
                {showDemo ? (
                  <Laptop className="h-3 w-3" />
                ) : (
                  <ImageIcon className="h-3 w-3" />
                )}
              </div>
            )} */}
          </div>

          <div className="relative flex items-center justify-between">
            {/* Link wrapping the main content area, merging Link and inner div */}
            <Link
              href={`/components/${item.library}/${item.name}`}
              className="relative flex items-center mb-1 p-4 group/link grow min-w-0 pr-10 pl-8" // Combined classes and added padding
              aria-label={`View details for ${item.name}`}
            >
              {/* Library Badge - Absolutely Positioned within the padded container */}
              <div className="absolute left-1.5 top-1/2 -translate-y-1/2 z-20 flex items-center text-xs font-medium rounded-full bg-background/80 backdrop-blur-sm border border-transparent size-5 pl-[5px] transition-all duration-300 ease-in-out group/badge hover:w-auto hover:py-0.5 hover:pr-2 hover:bg-accent hover:text-accent-foreground hover:border-border">
                <span
                  className="h-2 w-2 rounded-full flex-shrink-0"
                  style={{
                    backgroundColor: getLibraryColor(item.library as string),
                  }}
                />
                <span className="inline-block max-w-0 opacity-0 group-hover/badge:max-w-[200px] group-hover/badge:opacity-100 group-hover/badge:ml-1.5 transition-all duration-300 ease-in-out overflow-hidden whitespace-nowrap">
                  {formatLibraryName(item.library as string)}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-lg font-medium truncate">
                {formatLibraryName(item.name)}
              </h3>

              {/* Link Icon */}
              <LinkIcon className="shrink-0 ml-1.5 h-4 w-4 text-muted-foreground opacity-0 group-hover/link:opacity-100 transition-opacity duration-200" />
            </Link>
            {/* Star Button */}
            <Button
              type="button"
              variant="link"
              size="icon"
              className={cn(
                "px-6 h-8 w-8 transition-colors",
                isItemStarred
                  ? "text-primary hover:text-primary/90"
                  : "text-muted-foreground hover:text-foreground"
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleStar(item.id);
              }}
              aria-label={isItemStarred ? "Unstar component" : "Star component"}
            >
              <Heart
                className={cn("h-4 w-4", isItemStarred ? "fill-primary" : "")}
              />
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }
);

ComponentCard.displayName = "ComponentCard";

// Memoize the component
export const MemoizedComponentCard = memo(ComponentCard);
MemoizedComponentCard.displayName = "ComponentCard";
